/* ============================================================
   Trasse Families — the household console (Phase 2 shell).

   Six rooms: Today · Analytics · Reports · Hours · Billing · Settings.
   All six are real, and the last placeholder — the live map on Today — is
   gone with FamilyLiveRun.

   The rule this file was built on survives at a finer grain: never show a
   parent an empty thing they will read as broken. Analytics and Reports now
   apply it to individual NUMBERS, naming fuel and driving quality as absent
   rather than drawing them as zero, and the live panel applies it to the map
   itself — a parked car gets a sentence, not a stationary dot.

   The shell is FleetShell, the same one the school console uses, so the
   two never drift apart on layout or responsiveness. Only the brand block
   and the accent change.
   ============================================================ */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FleetShell, { FleetNavItem } from '../FleetShell';
import { useAuth } from '../../contexts/AuthContext';
import { OrgSubscription } from '../../services/useOrgSubscription';
import FamilyBillingPage from './FamilyBillingPage';
import FamilyHoursPage from './FamilyHoursPage';
import FamilySettingsPage from './FamilySettingsPage';
import FamilyAnalyticsPage from './FamilyAnalyticsPage';
import FamilyReportsPage from './FamilyReportsPage';
import FamilyLiveRun from './FamilyLiveRun';
import FamilyRefuelPanel from './FamilyRefuelPanel';
import FamilySchoolTripsPanel from './FamilySchoolTripsPanel';
import { daysUntil, fmtDay, useFamilyBusId, useFamilyOrg } from './useFamilyOrg';
import {
  FAM, FAMILY_FONT_CSS, UI_FONT, DISPLAY_FONT, FAMILY_PILOT_DAYS, FAMILY_PRICE_LABEL,
  FAMILY_PRICE_KES, FAMILY_ADDON_KES,
  TRACKING_ON, TRACKING_OFF, FAMILY_PAGE_WRAP,
} from './familyTheme';

interface Props {
  orgCode: string;
  orgName: string;
  sub: OrgSubscription;
}

type TabKey = 'today' | 'analytics' | 'reports' | 'hours' | 'billing' | 'settings';

const TABS: { key: TabKey; label: string; path: string }[] = [
  { key: 'today', label: 'Today', path: '/families' },
  { key: 'analytics', label: 'Analytics', path: '/families/analytics' },
  { key: 'reports', label: 'Reports', path: '/families/reports' },
  { key: 'hours', label: 'Hours', path: '/families/hours' },
  { key: 'billing', label: 'Billing', path: '/families/billing' },
  { key: 'settings', label: 'Settings', path: '/families/settings' },
];

function tabFromPath(pathname: string): TabKey {
  const hit = TABS.find((t) => t.path !== '/families' && pathname.startsWith(t.path));
  return hit ? hit.key : 'today';
}

/* --------------------------------------------------------------- pieces -- */
const Panel: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <section style={{ background: '#fff', border: `1px solid ${FAM.line}`, borderRadius: 20, padding: 'clamp(18px,3vw,26px)', ...style }}>
    {children}
  </section>
);

export default function FamilyDashboard({ orgCode, orgName, sub }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const org = useFamilyOrg(orgCode);
  /* Runs are queried by vehicle, and the household has exactly one — resolved
     once here so Analytics and Reports never each go looking for it. */
  const { busId } = useFamilyBusId(orgCode);
  const [menuOpen, setMenuOpen] = React.useState(false);

  const tab = tabFromPath(location.pathname);
  const navItems: FleetNavItem[] = TABS.map((t) => ({
    key: t.key,
    label: t.label,
    onClick: () => navigate(t.path),
  }));

  /* The pilot flag comes from useOrgSubscription (one source of truth for
     status), but the COUNT comes from pilotEndsAt when we have it: expirationDate
     is deliberately one day later than the last free day, so counting from it
     would promise a day that is not free. */
  const isPilot = sub.isPilot || org.isPilot;
  const pilotLeft = org.pilotEndsAt ? daysUntil(org.pilotEndsAt) : sub.pilotDaysLeft;

  const headerRight = (
    <>
      {sub.status !== 'loading' && isPilot && (
        <span
          title={`Your free days run to ${fmtDay(org.pilotEndsAt)}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 999,
            fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
            background: FAM.tint, color: FAM.accent, border: `1px solid ${FAM.tint2}`,
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: FAM.accent, display: 'inline-block' }} />
          {FAMILY_PILOT_DAYS} days free — {pilotLeft} left
        </span>
      )}
      {sub.status !== 'loading' && !isPilot && (
        <button
          type="button"
          onClick={() => navigate('/families/billing')}
          title="Manage your subscription"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 999,
            fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', border: 'none', cursor: 'pointer',
            background: FAM.accent, color: '#fff',
            boxShadow: '0 4px 13px rgba(0,112,127,.4)',
          }}
        >
          {/* 2026-09-13: this pill rendered the BASE label to every household, so one
              paying for the add-on saw KES 1,200 here and KES 1,500 on Billing — two
              prices for the same household, and this is the one on every page. Show what
              they actually owe. */}
          <span style={{ fontSize: 11 }}>◆</span>{' '}
          {org?.analyticsAddon
            ? `KES ${(FAMILY_PRICE_KES + FAMILY_ADDON_KES).toLocaleString()}/month`
            : FAMILY_PRICE_LABEL}
        </button>
      )}
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 10,
            border: 'none', background: 'transparent', cursor: 'pointer', font: 'inherit', fontSize: 13,
            color: FAM.ink3, maxWidth: 200,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="3.4" /><path d="M5 20a7 7 0 0 1 14 0" /></svg>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUser?.email || 'Account'}</span>
        </button>
        {menuOpen && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setMenuOpen(false)} />
            <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 20, background: '#fff', border: `1px solid ${FAM.line}`, borderRadius: 12, boxShadow: '0 12px 30px rgba(76,52,148,.16)', minWidth: 180, padding: '4px 0' }}>
              <button type="button" onClick={() => { navigate('/families/settings'); setMenuOpen(false); }}
                style={{ width: '100%', padding: '10px 16px', textAlign: 'left', background: 'none', border: 'none', font: 'inherit', fontSize: 13.5, color: FAM.ink2, cursor: 'pointer' }}>
                Settings
              </button>
              <button type="button" onClick={() => { logout(); setMenuOpen(false); }}
                style={{ width: '100%', padding: '10px 16px', textAlign: 'left', background: 'none', border: 'none', font: 'inherit', fontSize: 13.5, color: FAM.danger, cursor: 'pointer' }}>
                Sign out
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );

  /* ------------------------------------------------------------- bodies -- */
  let body: React.ReactNode;
  if (tab === 'billing') {
    body = <FamilyBillingPage orgCode={orgCode} orgName={orgName || org.name} sub={sub} />;
  } else if (tab === 'today') {
    body = (
      <div style={FAMILY_PAGE_WRAP}>
        <Panel>
          <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '.12em', color: FAM.accent }}>YOUR HOUSEHOLD</span>
          <h2 style={{ fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: 34, letterSpacing: '-.01em', margin: '10px 0 0' }}>
            {org.name || orgName || orgCode}
          </h2>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
            {[
              { k: 'Code', v: orgCode },
              ...(org.vehiclePlate ? [{ k: 'Car', v: org.vehiclePlate }] : []),
              ...(org.driverMode ? [{ k: 'Driving', v: org.driverMode === 'parent_drives' ? 'You' : org.driverMode === 'private_van' ? 'A van service' : 'A driver you employ' }] : []),
              ...(org.schoolName ? [{ k: 'School', v: org.schoolName }] : []),
            ].map((chip) => (
              <span key={chip.k} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 12px', borderRadius: 999, background: FAM.tint, fontSize: 13, fontWeight: 600, color: FAM.ink }}>
                <span style={{ color: FAM.muted, fontWeight: 700, fontSize: 11.5, letterSpacing: '.06em', textTransform: 'uppercase' }}>{chip.k}</span>
                {chip.v}
              </span>
            ))}
          </div>
          {isPilot && (
            <p style={{ margin: '16px 0 0', fontSize: 14.5, lineHeight: 1.6, color: FAM.ink2 }}>
              You are on your {FAMILY_PILOT_DAYS} free days — <b style={{ color: FAM.ink }}>{pilotLeft} left</b>, running to{' '}
              {fmtDay(org.pilotEndsAt)}. No card is stored, so nothing is charged when they end.
            </p>
          )}
        </Panel>

        <FamilyLiveRun busId={busId} />

        {/* Both render nothing at all until they have something to say. */}
        <FamilySchoolTripsPanel orgCode={orgCode} />
        <FamilyRefuelPanel orgCode={orgCode} />

        <Panel>
          <h3 style={{ margin: 0, fontSize: 16.5, fontWeight: 800 }}>What Trasse follows, and what it does not</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 18, marginTop: 14 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: FAM.pos }}>Tracked</div>
              <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13.5, lineHeight: 1.7, color: FAM.ink2 }}>
                {TRACKING_ON.map((t) => <li key={t}>{t}</li>)}
              </ul>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: FAM.ink3 }}>Never tracked</div>
              <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13.5, lineHeight: 1.7, color: FAM.ink2 }}>
                {TRACKING_OFF.map((t) => <li key={t}>{t}</li>)}
              </ul>
            </div>
          </div>
        </Panel>
      </div>
    );
  } else if (tab === 'analytics') {
    body = (
      <FamilyAnalyticsPage orgCode={orgCode} busId={busId} />
    );
  } else if (tab === 'reports') {
    body = (
      <FamilyReportsPage orgCode={orgCode} orgName={orgName || org.name} busId={busId} />
    );
  } else if (tab === 'hours') {
    body = <FamilyHoursPage orgCode={orgCode} />;
  } else {
    body = <FamilySettingsPage orgCode={orgCode} orgName={orgName || org.name} />;
  }

  return (
    <FleetShell
      active={tab}
      navItems={navItems}
      headerRight={headerRight}
      zana
      brand={{ title: 'Trasse Families', subtitle: 'Your household' }}
    >
      {/* Scoped to the shell that is on screen — this <style> unmounts with the
          household console, and the school console is never mounted beside it. */}
      <style>{`${FAMILY_FONT_CSS} .pear-shell { font-family: ${UI_FONT}; }`}</style>
      <div style={{ padding: 'clamp(16px,3vw,28px)', fontFamily: UI_FONT, color: FAM.ink }}>{body}</div>
    </FleetShell>
  );
}
